// frontend/src/ts/common/services/reauthRedirect.ts
//
// Redirect-mode auth-expiry handling.
//
// In redirect mode (NDC webview) there is no login modal. On a 401 the
// apiClient interceptor calls reportAuthExpired(), which sets the
// `authExpired` flag in the zustand store; LoadingPage renders the
// LoginPage from that flag. A store flag — rather than the window event
// this module used to dispatch — because zustand set() is synchronous,
// global, and needs no listener registration, and the store -> re-render
// path is demonstrably reliable on the device (the Online indicator uses
// it). The login itself uses the client-side flows (GIS redirect via POST
// /auth, or Firebase email/password), none of which need the server-side
// OAuth code exchange (/login -> /callback requires GOOGLE_OAUTH_CLIENT_SECRET,
// which NDC environments deliberately do not configure). Showing the login
// page in place also preserves the current deep link automatically, since
// the URL is never left.
//
// Popup mode keeps the existing modal flow; this module is a no-op there.

import {isTauriAvailable} from "@/common/utils/tauri";
import {useNeurotuneStore} from "@/common/store/neurotune";

/** Whether auth-expiry handling uses the redirect-mode path (event ->
 * LoginPage in place) rather than the popup login modal.
 *
 * Inside Tauri this is ALWAYS true, regardless of the resolved loginMode:
 * the NDC device flow is redirect-only, and the popup branch's login modal
 * cannot drive the device's Google round-trip — if loginMode ever resolves
 * to anything else in the webview (stale localStorage cache, a deep page
 * loaded without the ?lm= param), silently falling back to the modal path
 * left 401s with no recovery at all. Tauri presence is the authoritative
 * discriminator there.
 *
 * Outside Tauri the configured loginMode decides, as before. */
export function isRedirectAuthMode(): boolean {
    if (isTauriAvailable()) return true
    return window.BIOS?.loginMode === 'redirect'
}

/** Called by the apiClient interceptor on 401 (and by the response-less
 * watchdog below) in redirect mode. Sets the store flag LoadingPage renders
 * the LoginPage from. Idempotent: a burst of concurrent 401s from the
 * drainer/upload worker sets the flag once. Logs every decision path — the
 * webview console is mirrored into ndc.log, so a device repro shows exactly
 * where the chain breaks if it ever does. */
export function reportAuthExpired(source: string): void {
    if (!isRedirectAuthMode()) {
        console.warn(`[auth] ${source}: ignored, not redirect auth mode`, {
            isTauri: isTauriAvailable(),
            loginMode: window.BIOS?.loginMode,
        })
        return
    }
    if (useNeurotuneStore.getState().authExpired) return
    console.warn(`[auth] ${source}: authExpired -> login page`)
    useNeurotuneStore.getState().setAuthExpired(true)
}

// --- Response-less auth failures (Tauri only) ------------------------------
//
// Observed on the NDC device: after the auth cookie is deleted, WKWebView
// requests hang instead of receiving the server's JSON 401, and the axios
// timeout rejects them with no response object at all (ECONNABORTED /
// ERR_NETWORK). Those errors never reach the interceptor's 401 branch, so
// the event above never fires and the user is stranded on a tray that says
// Offline. Watchdog: in redirect mode, track every response-less failure and
// notify (same deduped event) once they persist — a network that is merely
// down recovers on its own and never triggers this, because successes reset
// the failure streak. Browser mode is untouched: response-less failures
// there mean the server is genuinely unreachable.

/** Response-less failures must persist this long before we treat them as an
 * auth failure. Spans ~4 probe cycles (probe interval 5 s, timeout 10 s):
 * a few seconds of wifi dead-air cannot trigger it, while a hung
 * unauthenticated request trips it within ~30-45 s. */
export const RESPONSELESS_AUTH_FAILURE_WINDOW_MS = 40_000

let responselessFailures: number[] = []
let lastApiResponseAt: number | null = null

/** Record any API response (success or error-with-status): evidence that
 * auth transport is alive, resetting the response-less watchdog. */
export function markApiResponse(): void {
    lastApiResponseAt = Date.now()
    responselessFailures = []
}

/** Test-only seam: replace the reachability probe (null restores default). */
let serverReachableOverride: (() => Promise<boolean>) | null = null
export function __setServerReachableForTests(fn: (() => Promise<boolean>) | null): void {
    serverReachableOverride = fn
}

/** Response-less failures may only mean re-auth is needed if the server is
 * actually reachable. The probe is a raw fetch of the REAL API endpoint
 * (/api/whoami) — but deliberately NOT the app's whoAmI() call:
 *
 * - credentials: 'omit' — no cookie is attached. The failure this watchdog
 *   detects is credentialed requests hanging after cookie loss, so a
 *   credentialed probe would hang too (and going through apiClient would
 *   feed the probe's result back into the watchdog itself). Without
 *   credentials the server answers 401 instantly — any answer proves
 *   reachability.
 * - mode: 'no-cors' — the opaque response resolves regardless of status,
 *   so CORS headers on the 401 are irrelevant (matters in dev, where the
 *   page origin and API origin differ).
 *
 * A genuine outage fails this too, and then we stay on the Offline tray
 * instead of stranding the user on a login page they cannot use. */
async function serverReachable(): Promise<boolean> {
    if (serverReachableOverride) return serverReachableOverride()
    try {
        const controller = new AbortController()
        const timer = window.setTimeout(() => controller.abort(), 8_000)
        try {
            await fetch(window.BIOS.neurotune_base_url + '/api/whoami', {
                method: 'GET',
                mode: 'no-cors',
                cache: 'no-store',
                credentials: 'omit',
                signal: controller.signal,
            })
            return true
        } finally {
            window.clearTimeout(timer)
        }
    } catch {
        return false
    }
}

let reachabilityCheckInFlight: Promise<boolean> | null = null

/** Record a response-less API failure and, in redirect mode, notify once
 * the failures persist across RESPONSELESS_AUTH_FAILURE_WINDOW_MS with no
 * intervening response AND the server proves reachable without credentials. */
export function trackResponselessFailure(): void {
    if (!isRedirectAuthMode()) return
    const now = Date.now()
    // A recent real response proves this is not an auth-transport failure.
    if (lastApiResponseAt !== null && now - lastApiResponseAt < RESPONSELESS_AUTH_FAILURE_WINDOW_MS) return
    responselessFailures.push(now)
    // Retain markers for two windows: the oldest marks WHEN the failures
    // started (it must survive until the window elapses), and this very call
    // proves they are still ongoing.
    responselessFailures = responselessFailures.filter((t) => now - t <= RESPONSELESS_AUTH_FAILURE_WINDOW_MS * 2)
    const oldest = responselessFailures[0]
    if (responselessFailures.length < 2 || now - oldest < RESPONSELESS_AUTH_FAILURE_WINDOW_MS) return
    if (reachabilityCheckInFlight) return
    reachabilityCheckInFlight = serverReachable().finally(() => {
        reachabilityCheckInFlight = null
    })
    reachabilityCheckInFlight.then((reachable) => {
        if (!reachable) return // genuine outage — stay on the Offline tray
        responselessFailures = []
        reportAuthExpired('persistent response-less failures (server reachable)')
    })
}

/** Test-only reset of the response-less watchdog state. */
export function __resetResponselessTrackingForTests(): void {
    responselessFailures = []
    lastApiResponseAt = null
    reachabilityCheckInFlight = null
    serverReachableOverride = null
}

// --- Post-login deep-link return (redirect mode) ---------------------------
//
// The GIS login_uri must stay byte-for-byte identical to the URI registered
// in Google Cloud Console (Google matches redirect URIs exactly, query
// string included), so the deep link cannot travel as a query parameter on
// it. Instead LoginPage stores the current path here before starting the
// Google round-trip, and LoadingPage consumes it after a successful whoAmI
// to navigate back to where the user came from.

export const POST_LOGIN_RETURN_KEY = 'neurotune_post_login_return'

/** Entries older than this are stale (an abandoned login must not redirect
 * a later, already-authenticated launch). */
export const POST_LOGIN_RETURN_MAX_AGE_MS = 10 * 60 * 1000

/** Navigation seam: replaces the top-frame URL. Replaceable in tests. */
type ReplaceFn = (url: string) => void
let replaceLocation: ReplaceFn = (url) => {
    window.location.replace(url)
}

/** Test-only override of the navigation function (null restores default). */
export function __setReplaceLocationForTests(fn: ReplaceFn | null): void {
    replaceLocation = fn ?? ((url) => {
        window.location.replace(url)
    })
}

/** Store the page the user is logging in from (redirect mode only).
 *
 * Written to BOTH storages on purpose: the GIS round-trip is a cross-site
 * navigation chain (app -> accounts.google.com -> /auth -> app), and
 * WKWebView (Tauri) may swap web processes across cross-site navigations
 * (PSON) — sessionStorage is per-process and does not survive that, so it
 * came back empty after login and the deep link was never resumed.
 * localStorage is disk-persisted per origin and survives process swaps, so
 * it is the durable copy; sessionStorage remains as a fallback where
 * localStorage is unavailable. */
export function storePostLoginReturnTo(): void {
    if (!isRedirectAuthMode()) return
    const raw = JSON.stringify({
        path: window.location.pathname + window.location.search,
        at: Date.now(),
    })
    try {
        localStorage.setItem(POST_LOGIN_RETURN_KEY, raw)
    } catch {
        // Storage unavailable — the deep link is a convenience, not a requirement.
    }
    try {
        sessionStorage.setItem(POST_LOGIN_RETURN_KEY, raw)
    } catch {
        // Same — best effort only.
    }
}

/** Read and clear the stored return path. Returns null when absent, stale,
 * malformed, or outside redirect mode. Same-origin paths only (must start
 * with a single "/") — anything else is dropped. */
export function consumePostLoginReturnTo(): string | null {
    if (!isRedirectAuthMode()) return null
    // localStorage first: it survives WKWebView cross-site process swaps;
    // sessionStorage is the fallback copy (see storePostLoginReturnTo).
    let raw: string | null = null
    try {
        raw = localStorage.getItem(POST_LOGIN_RETURN_KEY)
    } catch {
        // Fall through to sessionStorage.
    }
    if (!raw) {
        try {
            raw = sessionStorage.getItem(POST_LOGIN_RETURN_KEY)
        } catch {
            raw = null
        }
    }
    // One-shot: clear both copies regardless of which one held the value.
    try {
        localStorage.removeItem(POST_LOGIN_RETURN_KEY)
    } catch { /* best-effort cleanup */ }
    try {
        sessionStorage.removeItem(POST_LOGIN_RETURN_KEY)
    } catch { /* best-effort cleanup */ }
    if (!raw) return null
    try {
        const parsed = JSON.parse(raw) as { path?: unknown, at?: unknown }
        const path = parsed.path
        const at = parsed.at
        if (typeof path !== 'string' || !path.startsWith('/') || path.startsWith('//')) return null
        if (typeof at !== 'number' || Date.now() - at > POST_LOGIN_RETURN_MAX_AGE_MS) return null
        return path
    } catch {
        return null
    }
}

/** After a successful bootstrap, navigate back to the stored return path if
 * it differs from the current page. Returns true when a navigation started
 * (caller should stop its work). */
export function returnToPostLoginTargetIfNeeded(): boolean {
    const target = consumePostLoginReturnTo()
    if (!target) return false
    if (target === window.location.pathname + window.location.search) return false
    replaceLocation(target)
    return true
}
